import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { BrowserUtils } from "abweb/utils/BrowserUtils";
import { URLUtils } from "abweb/utils/URLUtils";

/** IE and EDGE are no longer supported */
export let SupportedBrowser = /*#__PURE__*/function (SupportedBrowser) {
  SupportedBrowser[SupportedBrowser["UNKNOWN"] = 0] = "UNKNOWN";
  SupportedBrowser[SupportedBrowser["CHROME"] = 1] = "CHROME";
  SupportedBrowser[SupportedBrowser["FIREFOX"] = 2] = "FIREFOX";
  SupportedBrowser[SupportedBrowser["IE"] = 3] = "IE";
  SupportedBrowser[SupportedBrowser["EDGE"] = 4] = "EDGE";
  SupportedBrowser[SupportedBrowser["EDGE_CHROMIUM"] = 5] = "EDGE_CHROMIUM";
  return SupportedBrowser;
}({});

/***
 * Detects current browser and version, and determining if the current browser is supported.
 *
 * Should NOT use browser/version to determine html or css sensitive code paths.
 * Instead, use modernizr to directly detect html/css features.
 * However, modernizr does not support browser and version detection, which is needed to ensure
 * browser is an Ab Initio supported browser (hence this class).
 */
export class BrowserCheck {
  // Do NOT use as proxy for html/css feature detection (use modernizr instead)
  static get browser() {
    if (BrowserCheck._browser == null) {
      BrowserCheck.init();
    }
    switch (BrowserCheck._browser.toUpperCase()) {
      case "CHROME":
        return SupportedBrowser.CHROME;
      case "FIREFOX":
        return SupportedBrowser.FIREFOX;
      case "IE":
        return SupportedBrowser.IE;
      case "EDGE":
        return SupportedBrowser.EDGE;
      case "EDGECHROMIUM":
        return SupportedBrowser.EDGE_CHROMIUM;
      default:
        return SupportedBrowser.UNKNOWN;
    }
  }

  /* Used to get the underlying string value, for errors */
  static get browserName() {
    if (BrowserCheck._browser == null) {
      BrowserCheck.init();
    }
    return BrowserCheck._browser;
  }

  // Do NOT use as proxy for html/css feature detection (use modernizr instead)
  static get version() {
    if (BrowserCheck._version == null) {
      BrowserCheck.init();
    }
    return this._version; // only expose for PFS
  }

  /** Only used in tests that verify that tests are running on supported ab initio browser: BrowserCheckUSpec.ts */
  static isSupportedBrowserVersion() {
    const currBrowser = BrowserCheck.browser;
    let minBrowserVersion = 9999;
    switch (currBrowser) {
      case SupportedBrowser.CHROME:
        minBrowserVersion = BrowserCheck.CHROME_MIN_VERSION;
        break;
      case SupportedBrowser.EDGE_CHROMIUM:
        minBrowserVersion = BrowserCheck.EDGE_CHROMIUM_MIN_VERSION;
        break;
      case SupportedBrowser.FIREFOX:
        minBrowserVersion = BrowserCheck.FIREFOX_MIN_VERSION;
        break;
      default:
        return false;
    }
    const currVersion = Number(BrowserCheck.version);
    return currVersion >= minBrowserVersion;
  }

  /** @return true iff current browser is in browsers array */
  static isBrowser(...browsers) {
    return browsers.indexOf(BrowserCheck.browser) >= 0;
  }
  static isEdgeChromium() {
    return BrowserCheck.browser === SupportedBrowser.EDGE_CHROMIUM;
  }
  static isChrome() {
    return BrowserCheck.browser === SupportedBrowser.CHROME;
  }
  static isFirefox() {
    return BrowserCheck.browser === SupportedBrowser.FIREFOX;
  }

  /**
   * Create a string suitable for PFS to report current state of the browser info
   * @returns {string}
   */
  static getBrowserText() {
    // CAT-1276: browserNameAndVersion is redundant, but makes it easier to Grep PFS files for specific combinations
    let answer = `Current Browser State:\r\n
browserName: ${BrowserCheck.browserName}\r\n
browserVersion: ${BrowserCheck.version}\r\n
browserNameAndVersion: ${BrowserCheck.browserName} ${BrowserCheck.version}\r\n
url: ${BrowserUtils.getLocationHref()}\r\n
\r\n
Minimum Supported Browser Versions:\r\n
CHROME_MIN_VERSION: ${BrowserCheck.CHROME_MIN_VERSION}\r\n
FIREFOX_MIN_VERSION: ${BrowserCheck.FIREFOX_MIN_VERSION}\r\n
EDGE_CHROMIUM_MIN_VERSION: ${BrowserCheck.EDGE_CHROMIUM_MIN_VERSION}\r\n
\r\n`;

    // Tell PFS if we're not really checking.
    if (URLUtils.readCookie("skipBrowserCheck")) {
      answer += "WARNING: skipBrowserCheck is ON\r\n\r\n";
    }

    // CAT-1276: add pixel ratio so we can get zoom, and some widths and heights for fun
    answer += `window.devicePixelRatio: ${window.devicePixelRatio}\r\n`;
    let fields = [];
    for (const field in navigator) {
      fields.push(field);
    }
    fields.sort();
    answer += "Navigator Properties:\r\n";
    for (const field of fields) {
      answer += `navigator.${field}: ${navigator[field]}\r\n`;
    }
    return answer;
  }

  /*
   * This code is also run in VerifyBrowserVersion.js.  Please keep this in sync with that.
   *
   *   This function is from the net...  we should probably use a real library function to detect this when
   *   we're not under the time gun.
   *   Original Stack Overflow posting: http://stackoverflow.com/a/2401861
   *
   *   Alternative approach: http://stackoverflow.com/questions/13478303/correct-way-to-use-modernizr-to-detect-ie
   */
  static init() {
    const myUserAgent = navigator.userAgent;
    let myBrowser = myUserAgent.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
    let myUserAgentRegExp;
    if (/trident/i.test(myBrowser[1])) {
      myUserAgentRegExp = /\brv[ :]+(\d+)/g.exec(myUserAgent) || [];
      BrowserCheck._browser = "IE";
      BrowserCheck._version = myUserAgentRegExp[1] || "";
      return;
    }
    if (myBrowser[1] === "Chrome") {
      myUserAgentRegExp = myUserAgent.match(/\b(OPR|Edge|Edg)\/(\d+)/);
      if (myUserAgentRegExp != null) {
        BrowserCheck._browser = myUserAgentRegExp[1] === "OPR" ? "Opera" : myUserAgentRegExp[1] === "Edge" ? "Edge" : "EdgeChromium";
        BrowserCheck._version = myUserAgentRegExp[2];
        return;
      }
    }
    myBrowser = myBrowser[2] ? [myBrowser[1], myBrowser[2]] : [navigator.appName, navigator.appVersion, "-?"];
    myUserAgentRegExp = myUserAgent.match(/version\/(\d+)/i);
    if (myUserAgentRegExp != null) {
      myBrowser.splice(1, 1, myUserAgentRegExp[1]);
    }
    BrowserCheck._browser = myBrowser[0];
    BrowserCheck._version = myBrowser[1];
    return;
  }
}
_defineProperty(BrowserCheck, "CHROME_MIN_VERSION", 126);
_defineProperty(BrowserCheck, "EDGE_CHROMIUM_MIN_VERSION", 126);
_defineProperty(BrowserCheck, "FIREFOX_MIN_VERSION", 128);
_defineProperty(BrowserCheck, "_browser", null);
_defineProperty(BrowserCheck, "_version", null);
//# sourceMappingURL=BrowserCheck.js.map
